import { useMemo, useRef, useState } from 'react';
import { GetUsersInLocationsResponse, StatusType } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { UsersQueries } from '@frontend/api-users';

type UseOfficeUsersParams = {
  locationIds: string[];
  /** Server-side search term used to filter users by name. */
  search?: string;
};

type OfficeUser = NonNullable<GetUsersInLocationsResponse['users']>[number];

type UseOfficeUsersResult = {
  /** id -> label map for the currently loaded (and active) users. Rebuilds as pages/search change. */
  activeOfficeUsers: Record<string, string>;
  /** Flat list of loaded users across all fetched pages. */
  officeUsers: OfficeUser[];
  /**
   * id -> label map accumulated across every page loaded for the current locations.
   * Unlike `activeOfficeUsers`, entries persist even when a user is filtered out by a later
   * search, so a chip for a previously selected user always has a label to display.
   * Resets only when the selected locations change.
   */
  labelMap: Record<string, string>;
  /** Total number of matching users on the server (respects the active search term). */
  totalCount: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
};

const PAGE_SIZE = 40;

const buildLabel = ({ firstName = '', lastName = '' }: OfficeUser) => `${firstName} ${lastName}`.trim();

export const useOfficeUsers = ({ locationIds, search = '' }: UseOfficeUsersParams): UseOfficeUsersResult => {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage = false,
    fetchNextPage,
  } = UsersQueries.useGetUsersInLocationsInfinite(
    locationIds.length > 0 ? locationIds : [],
    search,
    PAGE_SIZE,
    'firstname',
    'asc'
  );

  const officeUsers = useMemo(() => {
    return (data?.pages ?? []).flatMap((page) => page.users ?? []);
  }, [data]);

  const totalCount = useMemo(() => {
    const pages = data?.pages ?? [];
    return pages.length > 0 ? pages[pages.length - 1].totalCount ?? 0 : 0;
  }, [data]);

  const activeOfficeUsers = useMemo(() => {
    return officeUsers
      .filter(({ status }) => !status || status === StatusType.ACTIVE)
      .reduce((acc, user) => {
        if (user.userId) {
          acc[user.userId] = buildLabel(user);
        }
        return acc;
      }, {} as Record<string, string>);
  }, [officeUsers]);

  // Accumulate id -> label across every page so chip labels survive search filtering / scrolling.
  const locationKey = locationIds.join(',');
  const [labelMap, setLabelMap] = useState<Record<string, string>>({});
  const previousLocationKey = useRef(locationKey);

  // Reset when locations change
  if (previousLocationKey.current !== locationKey) {
    previousLocationKey.current = locationKey;
    setLabelMap({});
  }

  // Accumulate new entries
  for (const id in activeOfficeUsers) {
    if (labelMap[id] !== activeOfficeUsers[id]) {
      setLabelMap({ ...labelMap, ...activeOfficeUsers });
      break;
    }
  }

  return {
    activeOfficeUsers,
    officeUsers,
    labelMap,
    totalCount,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage: fetchNextPage,
  };
};
